import { RefObject, useCallback, useEffect, useRef } from 'react';

type UseFocusTrapProps = {
	ref: RefObject<HTMLElement>;
	enable?: boolean;
};

const TABBABLE_SELECTOR = [
	'a[href]',
	'a[tabindex]',
	'button',
	'input',
	'textarea',
	'select',
	'iframe',
]
	.map((s) => `${s}:not([tabindex="-1"]):not([disabled])`)
	.join(', ');

/*
 * When enabled, trap keyboard focus inside the container
 */
const useFocusTrap = ({ ref, enable = true }: UseFocusTrapProps) => {
	const previousActiveElement = useRef<HTMLElement | null>(null);

	const onKeyDown = useCallback(
		(e: KeyboardEvent) => {
			const container = ref.current;
			if (e.code !== 'Tab' || !container) {
				return;
			}

			const tabbables = container.querySelectorAll(
				TABBABLE_SELECTOR,
			) as NodeListOf<HTMLElement>;
			if (!tabbables?.length) {
				return;
			}

			const reverse = e.shiftKey;

			if (e.target === tabbables[tabbables.length - 1] && !reverse) {
				tabbables[0]?.focus();
				e.preventDefault();
			}

			if (e.target === tabbables[0] && reverse) {
				tabbables[tabbables.length - 1]?.focus();
				e.preventDefault();
			}
		},
		[ref],
	);

	const onFocusIn = useCallback(
		(e: Event) => {
			const tabbables = ref.current?.querySelectorAll(
				TABBABLE_SELECTOR,
			) as NodeListOf<HTMLElement>;
			const reverse = (e as KeyboardEvent).shiftKey;
			if (!ref.current?.contains(e.target as HTMLElement)) {
				// tabbed out of the container (from iframe) - focus the first/last tabbable element
				const index = reverse ? tabbables.length - 1 : 0;
				tabbables[index]?.focus();
			}
		},
		[ref],
	);

	const onEnable = useCallback(() => {
		addEventListener('keydown', onKeyDown);
		addEventListener('focusin', onFocusIn);
		previousActiveElement.current = document.activeElement as HTMLElement;
		(document.activeElement as HTMLElement)?.blur();
	}, [onKeyDown, onFocusIn]);

	const onDisable = useCallback(() => {
		previousActiveElement.current?.focus();
	}, []);

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		if (enable) {
			onEnable();
		} else {
			onDisable();
		}

		return () => {
			removeEventListener('keydown', onKeyDown);
			removeEventListener('focusin', onFocusIn);
		};
	}, [ref, enable]);
};

export default useFocusTrap;
